import React from "react";
import { Config } from "@nusrif/types";
import { loadConfigs } from "@nusrif/services";
import { ErrorPage, SplashScreen } from "@nusrif/ui/client";
import { useAppForeground } from "@nusrif/hooks";

const ConfigContext = React.createContext<Config | null>(null);

export const useConfig = (): Config => {
  const config = React.useContext(ConfigContext);
  if (!config) {
    throw new Error("useConfig must be used within a ConfigProvider");
  }
  return config;
};

interface Props {
  children: React.ReactNode;
}

export const ConfigProvider: React.FC<Props> = ({ children }) => {
  const [config, setConfig] = React.useState<Config | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  // Function to fetch config from API
  const fetchConfigFromAPI = React.useCallback(async () => {
    try {
      console.info("Attempting to fetch config from API...");
      const res = await loadConfigs();
      if (res.success) {
        localStorage.setItem("nusrif-config", JSON.stringify(res.data));
        setConfig(res.data);
        console.info("✅ Loaded config from API successfully");
        return true;
      } else {
        console.warn(
          "⚠️ API returned failure, using cached config. Response:",
          res
        );
        return false;
      }
    } catch (apiError) {
      console.warn(
        "❌ Failed to fetch config from API, using cached config. Error:",
        apiError
      );
      // Log additional details for debugging
      if (apiError && typeof apiError === "object") {
        console.warn("Error details:", {
          status: (apiError as any).status,
          message: (apiError as any).message,
          name: (apiError as any).name,
        });
      }
      return false;
    }
  }, []);

  // Function to load app config (initial load)
  const loadAppConfig = React.useCallback(async () => {
    try {
      // 1️⃣ Try to get cached config first
      const cached = localStorage.getItem("nusrif-config");
      if (cached) {
        setConfig(JSON.parse(cached));
        console.info("Loaded config from cache");
      }

      // 2️⃣ Fetch updated config from API
      await fetchConfigFromAPI();

      // 3️⃣ If no cached config and API failed, use default config
      if (!cached) {
        console.warn("No cached config available, using default config");
        // Import default config as fallback
        const { config: defaultConfig } = await import("@nusrif/config");
        // Convert base config to API config format
        const fallbackConfig: Config = {
          appCountryCode: defaultConfig.region.countryCode,
          desktopBanners: [
            {
              name: "Premium Banner",
              image:
                "https://nusrif-ecom-dev.blr1.cdn.digitaloceanspaces.com/nusrif-app-banners/PREMIUM.jpg",
              urlPath: "/",
            },
          ],
          landingBannersLong: [
            {
              name: "Exclusive Launch Offer: Premium Boxy-Fit T-Shirt",
              image:
                "https://nusrif-ecom-dev.blr1.cdn.digitaloceanspaces.com/nusrif-app-banners/qxiojrq2lnumjntoy4pz.webp",
              urlPath: "/product/23",
            },
            {
              name: "Exclusive Launch Offer: Premium Boxy-Fit T-Shirt",
              image:
                "https://nusrif-ecom-dev.blr1.cdn.digitaloceanspaces.com/nusrif-app-banners/oepk3m7pev2x8ykjcvwe.webp",
              urlPath: "/product/23",
            },
            {
              name: "Soft. Smooth. Simplified.",
              image:
                "https://nusrif-ecom-dev.blr1.cdn.digitaloceanspaces.com/nusrif-app-banners/swqvlsuc0iuqrbdroar2.webp",
              urlPath: "/store",
            },
          ],
          minimumOrderValueForFreeDelivery: "0",
          cod: "true",
          returnPeriodDays: "7",
          deliveryCharge: "0",
          minSupportedVersion: defaultConfig.app.version,
          latestVersion: defaultConfig.app.version,
          forceUpdate: false,
          playStoreUrl: "",
          appStoreUrl: "",
          privacyPolicyUrl: "",
          termsAndConditionsUrl: "",
          cancellationAndRefundPolicyURL: "",
          contactUsUrl: "",
          aboutUsUrl: "",
          faqUrl: "",
          marqueeText: defaultConfig.app.tagline,
        };
        setConfig(fallbackConfig);
      }
    } catch (err) {
      console.error("Failed to load app config:", err);
      setError("Failed to load configuration");
    }
  }, [fetchConfigFromAPI]);

  // Initial config load
  React.useEffect(() => {
    loadAppConfig();
  }, [loadAppConfig]);

  // Refetch config when app comes to foreground
  useAppForeground(() => {
    console.info("🔄 App came to foreground, refetching config...");
    fetchConfigFromAPI();
  });

  if (error) {
    return <ErrorPage />;
  }

  if (!config) {
    return <SplashScreen />;
  }

  return (
    <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>
  );
};
